import { usePublicLanguage } from "../context/PublicLanguage";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search as SearchIcon } from "lucide-react";
import { publicApi } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import StoryCard from "@/components/StoryCard";

const FILTERS = [
  ["all", "All"],
  ["ai", "AI"],
  ["robotics", "Robotics"],
  ["cybersecurity", "Cybersecurity"],
  ["future-tech", "Future Tech"],
  ["business", "Business"],
  ["guides", "Guides"],
];

export default function SearchPage() {
  const { t } = usePublicLanguage();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [filter, setFilter] = useState(params.get("category") || "all");

  useSEO({ title: "Search — TechDishes", description: "Search TechDishes for AI and technology stories." });

  useEffect(() => {
    setQ(params.get("q") || "");
  }, [params]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ["search", params.get("q") || "", filter],
    queryFn: () => publicApi.search({ q: params.get("q") || "", category: filter }),
  });

  const submit = (e) => {
    e.preventDefault();
    const p = {};
    if (q.trim()) p.q = q.trim();
    if (filter !== "all") p.category = filter;
    setParams(p);
  };

  return (
    <div className="mx-auto max-w-[1160px] px-5 md:px-8 py-10 md:py-14">
      <h1 className="font-display font-extrabold tracking-tight text-white text-4xl md:text-5xl mb-6" style={{ letterSpacing: "-0.03em" }}>{t('Search')}</h1>

      <form onSubmit={submit} className="relative mb-6">
        <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71767c]" />
        <input
          aria-label={t("Search")} data-testid="search-page-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('Search stories, topics, tags…')}
          className="w-full bg-[#111315] border rounded-xl pl-12 pr-4 py-4 text-white placeholder:text-[#71767c] outline-none focus:border-[#FF8A1F]/50 text-lg"
          style={{ borderColor: "rgba(255,255,255,0.1)" }}
          autoFocus
        />
      </form>

      <div className="flex flex-wrap gap-2 mb-10">
        {FILTERS.map(([slug, label]) => (
          <button
            key={slug}
            data-testid={`search-filter-${slug}`}
            onClick={() => {
              setFilter(slug);
              const p = {};
              if (q.trim()) p.q = q.trim();
              if (slug !== "all") p.category = slug;
              setParams(p);
            }}
            className="td-eyebrow px-4 py-2 rounded-full border transition-colors"
            style={{
              borderColor: filter === slug ? "#FF8A1F" : "rgba(255,255,255,0.1)",
              color: filter === slug ? "#FF8A1F" : "#A7ABB0",
              background: filter === slug ? "rgba(255,138,31,0.08)" : "transparent",
            }}
          >
            {t(label)}
          </button>
        ))}
      </div>

      {isFetching ? (
        <p className="text-[#A7ABB0]">{t('Searching…')}</p>
      ) : results.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-white text-xl font-display font-bold mb-2">{t("No results")}{params.get("q") ? ` ${t("for")} “${params.get("q")}”` : ""}</p>
          <p className="text-[#A7ABB0]">{t('Try a different keyword or category.')}</p>
        </div>
      ) : (
        <>
          <p className="td-eyebrow mb-6" style={{ color: "#71767c", fontSize: "0.6rem" }}>
            {results.length} {t(results.length === 1 ? "result" : "results")}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {results.map((a) => (
              <StoryCard key={a.slug} article={a} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
